import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { AuthService } from '../auth.service';
import { ConfigService } from '../config.service';
import { LoggingService } from '../logging.service';

const pickErrorMessage = (errorResponse: HttpErrorResponse): string => {
  const { message } = errorResponse.error;

  const emailErrorRegex = /User (\S+) will need to be added/;

  if (emailErrorRegex.test(message)) {
    let [, email] = message.match(emailErrorRegex) || [];
    email = email || 'the associated email address';
    return `Login was successful, but ${email} is not set up as a user within Flywheel. Contact an administrator to get registered to the platform.`;
  }

  if (/Email domain /.test(message)) {
    return `${message}. Contact an administrator to add your domain to the allow list.`;
  }

  if (/IdP /.test(message)) {
    return `${message}. Contact an administrator to add your IdP to the allow list.`;
  }

  if (/onboarding disabled/.test(message)) {
    return 'Could not log in because user onboarding is disabled. Please contact your administrator.';
  }

  return `Login was not successful. ${message}`;
};

/**
 * Checks if the user is authenticated,
 * redirects to login if not.
 *
 * Then checks if we can retrieve the user's info,
 * opens an error dialog if not.
 */
export const verifyUserGuard: CanActivateFn = (snapshot, state) => {
  const auth0 = inject(Auth0Service);
  const authService = inject(AuthService);
  const httpClient = inject(HttpClient);
  const logger = inject(LoggingService);
  const config = inject(ConfigService);
  const router = inject(Router);

  const self$ = httpClient.get('/api/users/self');

  const handleError = (errorResponse: HttpErrorResponse): void => {
    logger.warn(errorResponse);

    // this error arising in central implies a sync process error
    const isCentralSyncError = /onboarding disabled/.test(errorResponse.message) && config.getCentralConfig()?.enabled;

    // special case for central
    if (isCentralSyncError) {
      authService.logout(window.location.origin + '/#/logged-out/user-sync-error');
    } else {
      const message = pickErrorMessage(errorResponse);
      authService.logout(window.location.origin + '/#/logged-out/login-error?message=' + encodeURIComponent(message));
    }
  };

  if (!config.isAuth0Enabled()) {
    return of(authService.isLoggedIn).pipe(
      tap((loggedIn) => {
        if (!loggedIn) {
          router.navigate(['/login'], { queryParams: { redirect: state.url } });
        }
      }),
      switchMap(() => self$),
      map((user) => Boolean(user)), // if we get a non-error status with something back they're good
      catchError((errorResponse: HttpErrorResponse) => {
        handleError(errorResponse);
        return of(false);
      }),
    );
  }

  return auth0.isAuthenticated$.pipe(
    tap((loggedIn) => {
      if (!loggedIn) {
        auth0.loginWithRedirect({
          appState: { target: state.url },
        });
      }
    }),
    switchMap(() => self$),
    map((user) => Boolean(user)), // if we get a non-error status with something back they're good
    catchError((errorResponse: HttpErrorResponse) => {
      handleError(errorResponse);
      return of(false);
    }),
  );
};
